"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useWorkspaceStore = void 0;
const tslib_1 = require("tslib");
const react_1 = require("react");
const ui_common_translation_1 = require("@desygner/ui-common-translation");
const ui_core_api_gateway_1 = require("@desygner/ui-core-api-gateway");
const ui_editor_data_access_assets_1 = require("@desygner/ui-editor-data-access-assets");
const services_1 = require("../services");
const Workspace_context_1 = require("./Workspace.context");
const workspace_store_1 = require("./workspace.store");
function formatFolder(type, folder, t) {
    const folderType = {
        text: 'text',
        image: 'image',
        video: 'video',
        logo: 'logo',
        icon: 'icon',
    };
    return folder
        .filter((folder) => folder.data.types.includes(folderType[type]))
        .map((folder) => {
        var _a, _b;
        return ({
            id: folder.data.id,
            name: folder.data.name,
            groups: (_b = (_a = folder.groups) === null || _a === void 0 ? void 0 : _a.map((group) => ({
                id: group.id,
                name: group.name,
            }))) !== null && _b !== void 0 ? _b : [],
            subTitle: folder.data.size > 1
                ? t('tool.library.assets.folder_items', { count: folder.data.size })
                : t('tool.library.assets.folder_item'),
            assets: [],
        });
    });
}
function formatSubFolder(resources, t) {
    const data = {
        folders: [],
        assets: [],
    };
    if (!(resources === null || resources === void 0 ? void 0 : resources.length)) {
        return data;
    }
    return resources.reduce((acc, resource) => {
        var _a, _b;
        if (resource.is_deleted)
            return acc;
        if (resource.type.toLowerCase() === 'folder') {
            acc.folders.push({
                id: resource.data.id,
                name: resource.data.name,
                subTitle: resource.data.size > 1
                    ? t('tool.library.assets.folder_items', {
                        count: resource.data.size,
                    })
                    : t('tool.library.assets.folder_item'),
                assets: [],
                groups: (_b = (_a = resource.groups) === null || _a === void 0 ? void 0 : _a.map((group) => ({
                    id: group.id,
                    name: group.name,
                }))) !== null && _b !== void 0 ? _b : [],
            });
        }
        else {
            const source = resource.data.default_content ||
                resource.data.selected_thumbnail ||
                resource.data.source;
            if (source) {
                acc.assets.push({
                    id: resource.id,
                    name: resource.data.name,
                    source: resource.data.default_content ||
                        resource.data.selected_thumbnail ||
                        resource.data.source,
                    details: resource,
                });
            }
        }
        return acc;
    }, data);
}
function getSelectedFolder(state) {
    return state.navigation.reduce((acc, folderId) => {
        const nextLevel = acc.folders.find((folder) => folder.id === folderId);
        if (nextLevel) {
            return {
                title: nextLevel.name,
                folders: nextLevel.folders,
                assets: nextLevel.assets,
            };
        }
        return acc;
    }, {
        title: null,
        folders: state.folders,
        assets: state.assets,
    });
}
function useWorkspaceStore({ type, workspace, collection = ui_editor_data_access_assets_1.ResourceCollection.WORKSPACE, credentials, options, filter, }) {
    const { t } = (0, ui_common_translation_1.useTranslation)();
    const state = (0, Workspace_context_1.useWorkspaceAssets)();
    const dispatch = (0, Workspace_context_1.useWorkspaceAssetsDispatch)();
    (0, react_1.useMemo)(() => {
        // TODO: Remove this when the API Gateway is properly configured
        ui_editor_data_access_assets_1.assetContainer
            .get(ui_core_api_gateway_1.TYPES.ApiGateway)
            .configure(credentials.baseUrl, credentials.userToken, workspace);
    }, []);
    const serviceApi = (0, react_1.useMemo)(() => {
        if (collection === ui_editor_data_access_assets_1.ResourceCollection.WORKSPACE) {
            return ui_editor_data_access_assets_1.assetContainer.get(ui_editor_data_access_assets_1.TYPES.WorkspaceAssets);
        }
        return ui_editor_data_access_assets_1.assetContainer.get(ui_editor_data_access_assets_1.TYPES.UserAssets);
    }, [collection]);
    const workspaceService = (0, react_1.useMemo)(() => {
        return new services_1.WorkspaceService(serviceApi);
    }, [serviceApi]);
    const assetTypes = {
        text: 'texts',
        image: 'images',
        video: 'videos',
        logo: 'logos',
        icon: 'icons',
    };
    (0, react_1.useEffect)(() => {
        Promise.all([
            workspaceService.fetchFolders(filter, type, t('tool.library.folder')),
            workspaceService.fetchAssets(filter, type),
            serviceApi.fetchGroups(),
        ]).then(([folders, assets, groups]) => {
            var _a, _b;
            if ((folders === null || folders === void 0 ? void 0 : folders.length) &&
                folders.every((folder) => folder &&
                    typeof folder === 'object' &&
                    'status' in folder &&
                    folder.status === 'Forbidden')) {
                dispatch({
                    type: workspace_store_1.ActionTypes.STATUS_UPDATED,
                    payload: 'error',
                });
                return;
            }
            dispatch({
                type: workspace_store_1.ActionTypes.ROOT_FOLDER_LOADED,
                payload: {
                    folders: formatFolder(type, folders !== null && folders !== void 0 ? folders : [], t),
                    assets: ((_b = (_a = assets === null || assets === void 0 ? void 0 : assets.filter((asset) => !asset.is_deleted)) === null || _a === void 0 ? void 0 : _a.map((asset) => {
                        var _a;
                        return ({
                            id: asset.id,
                            name: (_a = asset.data.name) !== null && _a !== void 0 ? _a : asset.placeholder,
                            source: asset.data.default_content ||
                                asset.data.selected_thumbnail ||
                                asset.data.source,
                            details: asset,
                        });
                    })) !== null && _b !== void 0 ? _b : []).filter((asset) => {
                        return (asset.source &&
                            (assetTypes[type] !== 'videos' ||
                                !(options === null || options === void 0 ? void 0 : options.filterYoutube) ||
                                !asset.details.data.source ||
                                asset.details.data.source.includes('http')));
                    }),
                },
            });
            dispatch({
                type: workspace_store_1.ActionTypes.GROUP_LOADED,
                payload: groups.map((group) => ({
                    id: group.id,
                    name: group.name,
                })),
            });
        });
    }, []);
    const activeLevel = getSelectedFolder(state);
    return {
        title: activeLevel.title,
        navigation: state.navigation,
        status: state.status,
        folders: activeLevel.folders,
        assets: activeLevel.assets,
        search: state.search,
        actions: {
            uploadYoutubeVideoToFolder: (url) => tslib_1.__awaiter(this, void 0, void 0, function* () {
                var _a, _b, _c;
                dispatch({
                    type: workspace_store_1.ActionTypes.STATUS_UPDATED,
                    payload: 'loading',
                });
                const folder = state.navigation[state.navigation.length - 1];
                const payload = {
                    company_id: workspace,
                    type: 'Video',
                    folder,
                    data: {
                        source: url,
                    },
                };
                const response = yield serviceApi.addAsset('videos', payload);
                if (folder) {
                    serviceApi.fetchResourceFromFolder(folder.toString(), {}, true);
                }
                const assets = [
                    {
                        id: response.id,
                        name: (_a = response.data) === null || _a === void 0 ? void 0 : _a.name,
                        source: (_b = response.data.selected_thumbnail) !== null && _b !== void 0 ? _b : (_c = response.data) === null || _c === void 0 ? void 0 : _c.source,
                        details: response,
                    },
                ];
                dispatch({
                    type: workspace_store_1.ActionTypes.ASSETS_ADDED,
                    payload: {
                        assets,
                        folderId: folder,
                    },
                });
            }),
            uploadToFolder: (result, type) => tslib_1.__awaiter(this, void 0, void 0, function* () {
                var _d;
                dispatch({
                    type: workspace_store_1.ActionTypes.STATUS_UPDATED,
                    payload: 'loading',
                });
                const files = result.successful.map((payload) => ({
                    tempLink: decodeURIComponent(payload.uploadURL),
                    name: payload.name,
                    meta: payload.meta,
                }));
                const typeMap = {
                    text: 'Text',
                    image: 'Image',
                    video: 'Video',
                    logo: 'Logo',
                    icon: 'Icon',
                };
                const assetMap = {
                    text: 'texts',
                    image: 'images',
                    video: 'videos',
                    logo: 'logos',
                    icon: 'icons',
                };
                const folder = state.navigation[state.navigation.length - 1];
                const promises = [];
                for (const data of files) {
                    const dimensions = (_d = data === null || data === void 0 ? void 0 : data.meta) === null || _d === void 0 ? void 0 : _d['dimensions'];
                    const payload = {
                        company_id: workspace,
                        type: typeMap[type],
                        folder,
                        data: {
                            name: data.name,
                            source: data.tempLink,
                            dimensions,
                        },
                    };
                    if (type === 'video') {
                        payload.data.file = data.tempLink;
                        delete payload.data.source;
                    }
                    promises.push(serviceApi.addAsset(assetMap[type], payload));
                }
                const responses = yield Promise.all(promises);
                if (folder) {
                    serviceApi.fetchResourceFromFolder(folder.toString(), {}, true);
                }
                const assets = responses.map((response) => {
                    var _a, _b, _c, _d, _e, _f;
                    return ({
                        id: response.id,
                        name: (_a = response.data) === null || _a === void 0 ? void 0 : _a.name,
                        source: (_e = (_c = (_b = response.data) === null || _b === void 0 ? void 0 : _b.selected_thumbnail) !== null && _c !== void 0 ? _c : (_d = response.data) === null || _d === void 0 ? void 0 : _d.source) !== null && _e !== void 0 ? _e : (_f = response.data) === null || _f === void 0 ? void 0 : _f.file,
                        details: response,
                    });
                });
                dispatch({
                    type: workspace_store_1.ActionTypes.ASSETS_ADDED,
                    payload: {
                        assets,
                        folderId: folder,
                    },
                });
            }),
            createFolder: (name, type, groups) => tslib_1.__awaiter(this, void 0, void 0, function* () {
                var _e;
                dispatch({
                    type: workspace_store_1.ActionTypes.STATUS_UPDATED,
                    payload: 'loading',
                });
                const currentFolderId = state.navigation[state.navigation.length - 1];
                const payload = Object.assign(Object.assign(Object.assign({ company_id: workspace, data: {
                        name,
                        types: [type],
                    } }, (groups
                    ? {
                        groups: groups.map((group) => {
                            return {
                                id: group.id,
                                name: group.name,
                                is_adhoc: false,
                                is_default: false,
                                is_global: false,
                                is_guest: false,
                                is_member: false,
                                is_public: false,
                            };
                        }),
                    }
                    : {})), { type: 'Folder' }), (currentFolderId ? { folder: currentFolderId } : {}));
                const response = yield serviceApi.createFolders(JSON.stringify(payload));
                currentFolderId &&
                    serviceApi.fetchResourceFromFolder(currentFolderId.toString(), undefined, true);
                dispatch({
                    type: workspace_store_1.ActionTypes.FOLDER_CREATED,
                    payload: {
                        folderId: response === null || response === void 0 ? void 0 : response.data.id,
                        name: response === null || response === void 0 ? void 0 : response.data.name,
                        size: response.data.size,
                        parent: currentFolderId,
                        groups: (_e = response === null || response === void 0 ? void 0 : response.groups) !== null && _e !== void 0 ? _e : [],
                    },
                });
            }),
            navigateToFolder: (folderId) => {
                dispatch({
                    type: workspace_store_1.ActionTypes.STATUS_UPDATED,
                    payload: 'loading',
                });
                serviceApi
                    .fetchResourceFromFolder(folderId.toString(), {
                    limit: state.assetsLoadLimit,
                    first: 0,
                })
                    .then((folders) => {
                    dispatch({
                        type: workspace_store_1.ActionTypes.FOLDER_LOADED,
                        payload: Object.assign({ folderId, navigation: state.navigation }, formatSubFolder(folders, t)),
                    });
                });
            },
            navigateToParent: () => {
                dispatch({
                    type: workspace_store_1.ActionTypes.PARENT_FOLDER_LOADED,
                });
            },
            search: (query) => {
                if (!query || query.length < 3) {
                    dispatch({
                        type: workspace_store_1.ActionTypes.SEARCH_LOADED,
                        payload: {
                            term: '',
                            assets: [],
                        },
                    });
                    return;
                }
                dispatch({
                    type: workspace_store_1.ActionTypes.STATUS_UPDATED,
                    payload: 'loading',
                });
                const assetTypes = {
                    text: 'texts',
                    image: 'images',
                    video: 'videos',
                    logo: 'logos',
                    icon: 'icons',
                };
                serviceApi.search(assetTypes[type], query, { limit: 20 }).then((assets) => dispatch({
                    type: workspace_store_1.ActionTypes.SEARCH_LOADED,
                    payload: {
                        term: query,
                        assets: Array.isArray(assets)
                            ? assets
                                .filter((asset) => !asset.is_deleted)
                                .map((asset) => ({
                                id: asset.id,
                                name: asset.data.name,
                                source: asset.data.default_content ||
                                    asset.data.selected_thumbnail ||
                                    asset.data.source,
                                details: asset,
                            }))
                                .slice(0, 20)
                            : [],
                    },
                }), () => dispatch({
                    type: workspace_store_1.ActionTypes.SEARCH_LOADED,
                    payload: {
                        term: query,
                        assets: [],
                    },
                }));
            },
            fetchResourceFromCurrentFolder: (page) => {
                const currentFolderId = state.navigation[state.navigation.length - 1];
                if (!currentFolderId)
                    return Promise.resolve({ folders: [], assets: [] });
                return new Promise((resolve) => serviceApi
                    .fetchResourceFromFolder(currentFolderId.toString(), {
                    limit: state.assetsLoadLimit,
                    first: state.assetsLoadLimit * (page - 1),
                })
                    .then((folders) => {
                    const subFolder = formatSubFolder(folders, t);
                    dispatch({
                        type: workspace_store_1.ActionTypes.UPDATE_FOLDER,
                        payload: Object.assign({ folderId: currentFolderId }, subFolder),
                    });
                    resolve(subFolder);
                }));
            },
        },
    };
}
exports.useWorkspaceStore = useWorkspaceStore;
//# sourceMappingURL=useWorkspaceStore.js.map